import { onDesktop } from "@naf/teamscheme";
import { Close } from "@styled-icons/material/Close";
import {
  createContext,
  type HTMLAttributes,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import styled from "styled-components";
import { api } from "../../../api/ApiClient";
import { useClientBase } from "../../../api/ClientBase";
import { EVENTS } from "../../../tracking/EVENTS";
import { useTracking } from "../../../tracking/TrackingContext";

type HeaderVariant = "static" | "flow";

interface GlobalAlert {
  id: string;
  title: string;
  htmlContent: string;
}

interface HeaderAlertProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  alert: GlobalAlert;
  variant: HeaderVariant;
}

function hiddenKey(alert: GlobalAlert) {
  return `GlobalAlert.${alert.id}.hidden`;
}

function isHidden(alert: GlobalAlert) {
  if (typeof window === "undefined") return false;
  return !!window.localStorage.getItem(hiddenKey(alert));
}

function hide(alert: GlobalAlert) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(hiddenKey(alert), new Date().toISOString());
}

interface IHeaderAlertsContext {
  alerts: GlobalAlert[];
}

interface GetAlertsResponse {
  alerts: GlobalAlert[];
}

const HeaderAlertsContext = createContext<IHeaderAlertsContext | null>(null);

export function HeaderAlertsProvider({ children }: { children: ReactNode }) {
  const client = useClientBase<void, GetAlertsResponse | null>(
    useCallback(() => api.get<GetAlertsResponse>("alert"), []),
  );

  const { fetch } = client;

  useEffect(() => {
    fetch();
  }, [fetch]);

  return (
    <HeaderAlertsContext.Provider value={client.result}>
      {children}
    </HeaderAlertsContext.Provider>
  );
}

export function HeaderAlert({ variant }: { variant: HeaderVariant }) {
  const context = useContext(HeaderAlertsContext);
  const alerts = context?.alerts || [];

  if (alerts.length === 0) return null;

  return (
    <HeaderAlertWrapper>
      {alerts.map((alert) => (
        <InnerHeaderAlert key={alert.id} alert={alert} variant={variant} />
      ))}
    </HeaderAlertWrapper>
  );
}

const InnerHeaderAlert = styled(
  ({ alert, variant: _, ...props }: HeaderAlertProps) => {
    const [isVisible, setIsVisible] = useState(!isHidden(alert));
    const tracking = useTracking();

    function close() {
      setIsVisible(false);
      hide(alert);
      tracking.event({
        ...EVENTS.HEADER.GLOBAL_ALERT_HIDE,
        label: alert.title,
        value: alert.id,
      });
    }

    if (!isVisible) return null;

    return (
      <div {...props}>
        <header>
          <h2>{alert.title}</h2>
          <button type="button" onClick={close} aria-label="Lukk">
            <Close width={16} />
          </button>
        </header>
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: intentional */}
        <div dangerouslySetInnerHTML={{ __html: alert.htmlContent }} />
      </div>
    );
  },
)`
  margin: 0 auto;
  max-width: ${({ theme }) => theme.skeleton.maxWidth};
  padding-top: ${({ theme }) => theme.spacing.space16};
  padding-bottom: ${({ theme }) => theme.spacing.space16};

  & > header,
  & > div {
    padding-left: ${({ theme }) => theme.spacing.space16};
    padding-right: ${({ theme }) => theme.spacing.space16};

    ${({ theme }) => onDesktop`
      padding-left: ${theme.spacing.space24};
      padding-right: ${theme.spacing.space24};
    `}
  }

  h2 {
    ${({ theme }) => theme.fontStyle.article.articleTextHeader};
  }

  h2,
  p {
    margin: ${({ theme }) => theme.spacing.space8} 0;
  }

  a {
    text-decoration: underline;
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    button {
      display: block;
      margin: 0;
      padding: 0;
      background: none;
      border: none;
      cursor: pointer;

      svg {
        display: block;
      }
    }
  }
`;

const HeaderAlertWrapper = styled.div`
  background: ${({ theme }) => theme.nafColor.information.warningLight};

  & > ${InnerHeaderAlert}:not(:first-child) {
    padding-top: 0;
  }
`;
